import type { ApprovalDto, MindChatDraft, MindChatOpenDraft } from '../services/MendsVisionService';
import type { MindDraftStatus } from '../components/MindDraftCard';
import { readApiError } from './approvalContent';
import { approveConflictText } from './approvalLabels';

/**
 * Estado do card de rascunho do chat (MENDSCRM-139). Tudo aqui é estrutural: status, ids e campos do
 * contrato. Nenhuma função lê o texto da mensagem para decidir o que o vendedor quis — quem decide é o backend.
 */
export interface DraftCardState {
  draft: MindChatDraft;
  status: MindDraftStatus;
  message?: string;
  /** Aprovação já criada para este card (send_message). */
  approvalId?: number;
  /** O vendedor alterou o texto do LLM (textSource = user). */
  edited?: boolean;
  /** Texto ou PDF alterado depois de criar a aprovação: o próximo envio chama editApproval antes. */
  unsynced?: boolean;
  /** O vendedor quer o PDF junto (só vale com o plano `available`). Ausente = sim. */
  includePdf?: boolean;
  /** Chave de idempotência do card: gerada uma vez, salva com a sessão e enviada em todo createApproval. */
  idempotencyKey?: string;
}

/** Mensagem mínima para as funções daqui (o componente tem a sua própria, mais rica). */
export interface DraftCarrier {
  id?: string;
  draft?: DraftCardState;
}

export const QUEUED_MESSAGE = 'Na fila — abra Aprovações e clique Ok para executar.';
export const SENT_FALLBACK_MESSAGE = 'Enviado.';
export const RELOAD_UNKNOWN_SEND_MESSAGE = 'Não sei se foi enviado — confira em Conversas antes de tentar de novo.';
export const DISCARDED_QUEUED_MESSAGE = 'Rascunho descartado; o item continua na fila.';
export const CREATE_FAILED_MESSAGE = 'Não consegui criar a aprovação. Nada foi enviado.';
export const SEND_FAILED_MESSAGE = 'Não consegui concluir o envio — confira em Conversas antes de tentar de novo.';
export const DRAFT_SUGGESTED_ACTION = 'Aprovar para enviar';
export const SUPERSEDED_DRAFT_MESSAGE = 'O cliente respondeu depois do rascunho — nada foi enviado. Peça um novo rascunho.';
export const APPROVED_UNCONFIRMED_MESSAGE = 'Aprovado, mas não confirmei o envio — confira em Conversas.';

const OPEN_STATUSES: ReadonlySet<MindDraftStatus> = new Set<MindDraftStatus>(['draft', 'editing']);
/** Status que um rascunho novo substitui (não está em voo nem terminou). */
const REPLACEABLE_STATUSES: ReadonlySet<MindDraftStatus> = new Set<MindDraftStatus>(['draft', 'editing', 'error']);

function hasText(draft: MindChatDraft): boolean {
  return (draft.text ?? '').trim().length > 0;
}

/** Chave de idempotência nova (até 64 caracteres): crypto.randomUUID quando existe, senão tempo + aleatório. */
export function newIdempotencyKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const rand = () => Math.random().toString(36).slice(2, 12);
  return `mk-${Date.now().toString(36)}-${rand()}-${rand()}`;
}

/**
 * Último card aberto (draft/editing ou pergunta pendente); cards em fila, enviados, em erro ou descartados não
 * contam, nem um card que já tem item na fila (approvalId): esse segue o próprio caminho e nunca é substituído.
 */
export function lastOpenDraftState(messages: readonly DraftCarrier[]): DraftCardState | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const state = messages[i].draft;
    if (state && !state.approvalId && OPEN_STATUSES.has(state.status)) return state;
  }
  return undefined;
}

/** Contexto estrutural do rascunho aberto que vai ao backend junto da próxima mensagem. */
export function openDraftFromMessages(messages: readonly DraftCarrier[]): MindChatOpenDraft | undefined {
  const state = lastOpenDraftState(messages);
  if (!state) return undefined;
  const { draft } = state;
  const open: MindChatOpenDraft = { kind: draft.kind, contactId: draft.contactId };
  const name = (draft.contactName ?? '').trim();
  if (name) open.contactName = name;
  if (hasText(draft)) {
    open.text = draft.text as string;
  } else if (draft.awaiting === 'value') {
    open.awaiting = 'value';
  } else {
    return undefined;
  }
  return open;
}

/**
 * Payload do send_message existente (D4) e a origem do texto para a telemetria. O PDF só vai com o plano
 * `available` e a caixa "Enviar o PDF junto" marcada.
 */
export function draftApprovalPayload(
  draft: MindChatDraft,
  edited: boolean,
  includePdf = true,
): { actionPayload: string; textSource: 'llm' | 'user' } {
  return {
    actionPayload: JSON.stringify({
      contactId: draft.contactId,
      text: draft.text ?? '',
      origin: 'mind_draft',
      pdf: draft.pdf?.status === 'available' && includePdf,
    }),
    textSource: edited ? 'user' : 'llm',
  };
}

export function draftApprovalTitle(draft: MindChatDraft): string {
  const label = draft.kind === 'follow_up' ? 'Follow-up' : 'Proposta';
  const name = (draft.contactName ?? '').trim();
  return name ? `${label} para ${name}` : label;
}

/**
 * Um rascunho novo substitui os cards ainda abertos (só o último rascunho é o contexto do chat).
 * Cards em voo, na fila ou enviados ficam como estão, e um card que já tem item na fila (approvalId, ex.: erro
 * depois de CONTEXT_UNVERIFIED) nunca é descartado automaticamente: o item segue vivo e o card é o único lugar
 * para tentar de novo ou descartar com aviso.
 */
export function discardOpenDrafts<M extends DraftCarrier>(messages: M[]): M[] {
  let changed = false;
  const out = messages.map((m) => {
    if (!m.draft || m.draft.approvalId || !REPLACEABLE_STATUSES.has(m.draft.status)) return m;
    changed = true;
    return { ...m, draft: discardedState(m.draft) };
  });
  return changed ? out : messages;
}

export function discardedState(state: DraftCardState): DraftCardState {
  return {
    ...state,
    status: 'discarded',
    message: state.approvalId ? DISCARDED_QUEUED_MESSAGE : undefined,
    unsynced: undefined,
  };
}

/**
 * Card de um rascunho recém-chegado (chave de idempotência nova, PDF marcado). Quando ele repete o texto do card
 * aberto do mesmo contato (send_only devolve o mesmo texto com usedFacts vazio), herda os fatos, a autoria do texto
 * e a escolha do PDF do card anterior.
 */
export function newDraftState(draft: MindChatDraft, previousOpen?: DraftCardState): DraftCardState {
  const prev = previousOpen?.draft;
  const sameText = !!prev
    && prev.contactId === draft.contactId
    && prev.kind === draft.kind
    && hasText(draft)
    && prev.text === draft.text;
  const idempotencyKey = newIdempotencyKey();
  if (!sameText || !previousOpen) return { draft, status: 'draft', includePdf: true, idempotencyKey };
  const usedFacts = (draft.usedFacts ?? []).length > 0 ? draft.usedFacts : (prev?.usedFacts ?? []);
  const state: DraftCardState = {
    draft: { ...draft, usedFacts },
    status: 'draft',
    includePdf: previousOpen.includePdf !== false,
    idempotencyKey,
  };
  if (previousOpen.edited) state.edited = true;
  return state;
}

/**
 * Card lido de uma sessão salva: envio em voo nunca volta a ficar clicável sem aviso. A chave de idempotência
 * salva é mantida; card salvo antes dela ganha uma agora (e a sessão a guarda no próximo salvamento).
 */
export function restoreDraftState(saved: DraftCardState): DraftCardState {
  const state = saved.idempotencyKey ? saved : { ...saved, idempotencyKey: newIdempotencyKey() };
  if (state.status === 'sending') {
    return { ...state, status: 'error', message: RELOAD_UNKNOWN_SEND_MESSAGE };
  }
  if (state.status === 'editing') {
    return { ...state, status: 'draft' };
  }
  return state;
}

/**
 * Card depois de o Aprovar recusar. SUPERSEDED é terminal no backend (a aprovação morreu): o card fica
 * descartado e sem approvalId, para nenhum clique recriar uma proposta vencida com um contexto novo.
 * Os demais erros (ex.: CONTEXT_UNVERIFIED, que devolve o item à fila) deixam tentar de novo.
 */
export function approveFailurePatch(error: unknown): Partial<DraftCardState> {
  if ((readApiError(error).code ?? '').toUpperCase() === 'SUPERSEDED') {
    return { status: 'discarded', message: SUPERSEDED_DRAFT_MESSAGE, approvalId: undefined, unsynced: undefined };
  }
  return { status: 'error', message: approveFailureMessage(error) };
}

/** Card depois de o Aprovar responder: só `executed === true` pode dizer enviado. */
export function approveResultPatch(
  result: Pick<ApprovalDto, 'executed' | 'executionResult' | 'executionError'> | null | undefined,
): Partial<DraftCardState> {
  if (result?.executionError) return { status: 'error', message: result.executionError };
  if (result?.executed === true) return { status: 'sent', message: result.executionResult || SENT_FALLBACK_MESSAGE };
  return { status: 'error', message: APPROVED_UNCONFIRMED_MESSAGE };
}

/** Texto do erro do Aprovar: a frase do servidor; sem ela, o texto padrão do código (nada foi enviado). */
export function approveFailureMessage(error: unknown): string {
  const info = readApiError(error);
  if (info.message) return info.message;
  const code = (info.code ?? '').toUpperCase();
  if (code === 'SUPERSEDED' || code === 'CONTEXT_UNVERIFIED') {
    return approveConflictText(info.code, info.reason).label;
  }
  return SEND_FAILED_MESSAGE;
}

/**
 * Erro ao criar a aprovação. Só uma recusa 4xx garante que nada saiu (o servidor pode executar uma rotina
 * na criação): sem resposta HTTP ou com 5xx, o resultado é desconhecido e o texto manda conferir.
 */
export function createFailureMessage(error: unknown): string {
  const info = readApiError(error);
  if (info.status !== null && info.status >= 400 && info.status < 500) {
    return info.message ?? CREATE_FAILED_MESSAGE;
  }
  return RELOAD_UNKNOWN_SEND_MESSAGE;
}
